import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getAuthTokenRequestOptions } from '../lib/authToken';

/**
 * Decode the JWT payload without verifying the signature. We only use this to
 * read claims — the access token is the same one the API validates on every request.
 */
function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

type JwtClaims = Record<string, unknown>;

/** Permissions implied by Auth0 role *names* when RBAC permissions are not in the token. */
const ROLE_PERMISSIONS: Record<string, readonly string[]> = {
  admin: [
    'operations:read',
    'operations:write',
    'api:read',
    'api:write',
    'assessments:read',
    'assessments:write',
  ],
  developer: ['api:read', 'api:write', 'assessments:read', 'assessments:write'],
  editor: ['assessments:read', 'assessments:write'],
  viewer: [],
};

function normalizeRoleKey(role: string): keyof typeof ROLE_PERMISSIONS | null {
  const compact = role.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (compact.includes('admin')) return 'admin';
  if (compact.includes('developer') || compact === 'dev') return 'developer';
  if (compact.includes('editor')) return 'editor';
  if (compact.includes('viewer')) return 'viewer';
  return null;
}

function permissionsFromRoleNames(roles: string[]): string[] {
  const out = new Set<string>();
  for (const role of roles) {
    const key = normalizeRoleKey(role);
    if (!key) continue;
    for (const perm of ROLE_PERMISSIONS[key]) out.add(perm);
  }
  return [...out];
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(/\s+/).filter(Boolean);
  }
  return [];
}

function rolesClaimKeys(audience: string | undefined): string[] {
  const keys = ['roles'];
  if (audience) {
    const base = audience.endsWith('/') ? audience.slice(0, -1) : audience;
    keys.push(`${base}/roles`);
  }
  return keys;
}

/**
 * Collect RBAC permissions from every common Auth0 claim location, then fall back
 * to role names when the API permissions array is missing (roles assigned but
 * "Add Permissions in the Access Token" not enabled, or permissions not on roles).
 */
export function resolvePermissionsFromClaims(
  accessClaims: JwtClaims | null,
  idClaims: JwtClaims | null | undefined,
  audience: string | undefined
): string[] {
  const merged = new Set<string>();

  for (const claims of [accessClaims, idClaims]) {
    if (!claims) continue;

    for (const perm of asStringArray(claims.permissions)) merged.add(perm);

    const scope = claims.scope;
    if (typeof scope === 'string') {
      for (const perm of scope.split(/\s+/).filter(Boolean)) merged.add(perm);
    }

    for (const key of rolesClaimKeys(audience)) {
      for (const role of asStringArray(claims[key])) {
        for (const perm of permissionsFromRoleNames([role])) merged.add(perm);
      }
    }
  }

  return [...merged];
}

let cachedPermissions: string[] | null = null;

/**
 * Role hierarchy for CREED admin.
 *
 * Section visibility is driven by `:read` permissions (cumulative — each tier
 * inherits from the one below):
 *   Viewer     — Dashboard + Tools (no permission required; all authenticated users)
 *   Editor     — Viewer + Assessments + Reference Data  (assessments:read)
 *   Developer  — Editor + API section                   (api:read)
 *   Admin      — Developer + Operations                 (operations:read)
 *
 * Mutation buttons inside each section are gated independently by `:write`
 * permissions. Roles in Auth0 should grant the matching write permission to
 * every tier that needs to mutate that resource:
 *   assessments:write — create/edit/delete assessments + reference data
 *   api:write         — create/rotate/revoke/delete API keys
 *   operations:write  — currently unused (reserved for future ops actions)
 *
 * Recommended Auth0 role assignments:
 *   Viewer    — (none)
 *   Editor    — assessments:read, assessments:write
 *   Developer — assessments:read, assessments:write, api:read, api:write
 *   Admin     — all of the above + operations:read, operations:write
 *
 * Auth0 API settings: enable RBAC, "Add Permissions in the Access Token", and
 * assign the permissions above to each role (role names alone also work via fallback).
 */
export function useUserPermissions(): {
  permissions: string[] | null;
  loading: boolean;
  can: (perm: string) => boolean;
  isViewer: boolean;
  isEditor: boolean;
  isDeveloper: boolean;
  isAdmin: boolean;
  /** Can mutate assessments + reference data. */
  canWriteAssessments: boolean;
  /** Can create/rotate/revoke/delete API keys. */
  canWriteApi: boolean;
  /** Reserved for future Operations-section mutations. */
  canWriteOperations: boolean;
  /** Highest role label for display. */
  roleLabel: 'Admin' | 'Developer' | 'Editor' | 'Viewer';
} {
  const { isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [permissions, setPermissions] = useState<string[] | null>(cachedPermissions);
  const [loading, setLoading] = useState(cachedPermissions === null);

  useEffect(() => {
    if (!isAuthenticated) {
      setPermissions([]);
      setLoading(false);
      cachedPermissions = null;
      return;
    }

    let cancelled = false;
    setLoading(cachedPermissions === null);

    const audience = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined;

    async function loadPermissions(forceRefresh: boolean): Promise<string[]> {
      const token = await getAccessTokenSilently({
        ...getAuthTokenRequestOptions(),
        ...(forceRefresh ? { cacheMode: 'off' as const } : {}),
      });
      const idClaims = await getIdTokenClaims().catch(() => undefined);
      const accessClaims = decodeJwtPayload<JwtClaims>(token);
      return resolvePermissionsFromClaims(accessClaims, idClaims ?? undefined, audience);
    }

    (async () => {
      try {
        let perms = await loadPermissions(false);
        if (perms.length === 0) {
          perms = await loadPermissions(true);
        }
        if (cancelled) return;
        cachedPermissions = perms;
        setPermissions(perms);
      } catch {
        if (cancelled) return;
        cachedPermissions = null;
        setPermissions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, getIdTokenClaims]);

  const can = useCallback(
    (perm: string): boolean => (permissions ?? []).includes(perm),
    [permissions]
  );

  const isAdmin = can('operations:read');
  const isDeveloper = isAdmin || can('api:read');
  const isEditor = isDeveloper || can('assessments:read');
  const isViewer = true; // all authenticated users

  console.log('isAdmin', isAdmin);
  console.log('isDeveloper', isDeveloper);
  console.log('isEditor', isEditor);
  console.log('isViewer', isViewer);
  console.log('permissions', permissions);

  const canWriteAssessments = can('assessments:write');
  const canWriteApi = can('api:write');
  const canWriteOperations = can('operations:write');

  const roleLabel: 'Admin' | 'Developer' | 'Editor' | 'Viewer' = isAdmin
    ? 'Admin'
    : isDeveloper
      ? 'Developer'
      : isEditor
        ? 'Editor'
        : 'Viewer';

  return {
    permissions,
    loading,
    can,
    isViewer,
    isEditor,
    isDeveloper,
    isAdmin,
    canWriteAssessments,
    canWriteApi,
    canWriteOperations,
    roleLabel,
  };
}

export function clearPermissionsCache(): void {
  cachedPermissions = null;
}
